import { useAuth } from "@/_core/hooks/useAuth";
import SalviaLayout from "@/components/SalviaLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { trpc } from "@/lib/trpc";
import { Plus, ExternalLink, CheckCircle2, XCircle, Clock, Copy, Play, StopCircle, AlertCircle, X, Filter, Loader2 } from "lucide-react";
import { useState, useMemo, useEffect } from "react";
import { useLocation } from "wouter";
import { toast as sonnerToast } from "sonner";
import { ConfirmActivateCampaignDialog } from "@/components/ConfirmActivateCampaignDialog";
import { ComboboxSelect } from "@/components/ui/combobox-select";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { usePageTitle } from "@/hooks/usePageTitle";

export default function Questionarios() {
  usePageTitle("Questionários");
  const { user } = useAuth();
  const [, setLocation] = useLocation();

  // Estados dos filtros
  const [selectedChannelId, setSelectedChannelId] = useState<number | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");

  // Debounce do campo de busca geral (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Estado do modal de confirmação de ativação
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [campaignToActivate, setCampaignToActivate] = useState<number | null>(null);

  // Estado do modal de confirmação de encerramento
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [campaignToClose, setCampaignToClose] = useState<{ id: number; clientName: string } | null>(null);

  const isAdminSalvia = user?.role === "admin" || user?.role === "salvia_operation_admin";
  const isAdminCanal = user?.role === "channel_admin" || user?.role === "channel_admin_multi";
  const isStructuralChannel = user?.role === "channel_admin" || user?.role === "channel_admin_multi" || user?.role === "channel_consultant";
  // Papel estrutural de Canal permanece somente com leitura neste módulo.
  const canManageCampaigns = isAdminSalvia;

  // Consultor Canal continua sem acesso ao módulo; Canal admin/multi acessa apenas em modo leitura.
  if (user?.role === "channel_consultant") {
    return (
      <SalviaLayout title="Questionários">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center space-y-4">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto" />
            <h2 className="text-xl font-semibold text-muted-foreground">Acesso Restrito</h2>
            <p className="text-sm text-muted-foreground">Seu perfil não possui acesso ao módulo de Questionários.</p>
            <Button variant="outline" onClick={() => setLocation("/home/canal")}>Voltar ao Início</Button>
          </div>
        </div>
      </SalviaLayout>
    );
  }

  // Listar canais (somente Admin Sálvia)
  const { data: channels, isLoading: loadingChannels } = trpc.channels.list.useQuery(
    undefined,
    { enabled: isAdminSalvia }
  );

  // Listar clientes para o filtro
  const { data: clientsResponse, isLoading: loadingClients } = trpc.clients.list.useQuery(
    {
      channelId: isAdminSalvia ? selectedChannelId ?? undefined : undefined,
      status: "active",
      limit: 100,
      offset: 0,
    },
    {
      enabled: isAdminCanal || (isAdminSalvia && !!selectedChannelId) || !isAdminSalvia,
    }
  );

  const clients = (clientsResponse as any)?.items || [];

  // Listar campanhas com a nova procedure (visão global com filtros)
  const { data: campaignsResponse, isLoading: loadingCampaigns, error: campaignsError, refetch: refetchCampaigns } = trpc.questionnaire.campaign.list.useQuery(
    {
      channelId: selectedChannelId ?? undefined,
      companyId: selectedClientId ?? undefined,
      status: selectedStatus ? (selectedStatus as "DRAFT" | "ACTIVE" | "CLOSED") : undefined,
      search: debouncedSearch.trim() !== "" ? debouncedSearch : undefined,
      limit: 100,
      offset: 0,
    }
  );

  const campaigns = campaignsResponse?.items ?? [];
  const totalCampaigns = campaignsResponse?.total ?? 0;

  // Mapa de clientes com campanha ativa (para desabilitar botão Ativar)
  const clientsWithActiveCampaign = useMemo(() => {
    const map = new Set<number>();
    campaigns.forEach((campaign: any) => {
      if (campaign.status === "ACTIVE" && campaign.companyId) {
        map.add(campaign.companyId);
      }
    });
    return map;
  }, [campaigns]);

  // Mutation para ativar campanha
  const activateMutation = trpc.questionnaire.campaign.activate.useMutation({
    onSuccess: () => {
      sonnerToast.success("Campanha Ativada", {
        description: "Link público gerado com sucesso!",
      });
      refetchCampaigns();
    },
    onError: (error) => {
      if (error.message === "CAMPAIGN_ALREADY_ACTIVE") {
        sonnerToast.error("Campanha Já Ativa", {
          description: "Já existe uma campanha ativa para este cliente. Encerre a campanha atual antes de ativar outra.",
        });
      } else {
        sonnerToast.error("Erro ao Ativar", {
          description: error.message,
        });
      }
    },
  });

  // Mutation para encerrar campanha
  const closeMutation = trpc.questionnaire.campaign.closeAndConsolidate.useMutation({
    onSuccess: (data) => {
      setCloseDialogOpen(false);
      setCampaignToClose(null);

      if (data.alreadyClosed) {
        sonnerToast.info("Campanha Já Encerrada", {
          description: "Esta campanha já havia sido encerrada anteriormente.",
        });
      } else {
        const modeInfo = data.modeConsolidation
          ? ` ${data.modeConsolidation.totalGroups} grupo(s) consolidado(s), ${data.modeConsolidation.totalModes} moda(s) calculada(s).`
          : "";
        const blockedInfo = data.modeConsolidation && data.modeConsolidation.blockedGroups > 0
          ? ` ${data.modeConsolidation.blockedGroups} grupo(s) bloqueado(s) por kMin.`
          : "";

        sonnerToast.success("Campanha Encerrada", {
          description: `Coleta encerrada e consolidação por moda concluída.${modeInfo}${blockedInfo}`,
          duration: 8000,
        });
      }
      refetchCampaigns();
    },
    onError: (error) => {
      setCloseDialogOpen(false);
      setCampaignToClose(null);
      sonnerToast.error("Erro ao Encerrar", {
        description: error.message,
      });
    },
  });

  const handleActivate = (campaignId: number) => {
    setCampaignToActivate(campaignId);
    setConfirmDialogOpen(true);
  };

  const handleConfirmActivate = async () => {
    if (!campaignToActivate) return;
    
    return new Promise<void>((resolve, reject) => {
      activateMutation.mutate(
        { campaignId: campaignToActivate },
        {
          onSuccess: () => {
            setConfirmDialogOpen(false);
            setCampaignToActivate(null);
            resolve();
          },
          onError: (error) => {
            setConfirmDialogOpen(false);
            setCampaignToActivate(null);
            reject(error);
          },
        }
      );
    });
  };

  const handleClose = (campaignId: number, clientName: string) => {
    setCampaignToClose({ id: campaignId, clientName });
    setCloseDialogOpen(true);
  };

  const handleConfirmClose = () => {
    if (!campaignToClose) return;
    closeMutation.mutate({ campaignId: campaignToClose.id });
  };

  const handleCopyLink = (publicToken: string) => {
    const url = `${window.location.origin}/q/${publicToken}`;
    navigator.clipboard.writeText(url);
    sonnerToast.success("Link Copiado", {
      description: "Link público copiado para a área de transferência!",
    });
  };

  const handleClearFilters = () => {
    setSelectedChannelId(null);
    setSelectedClientId(null);
    setSelectedStatus(null);
    setSearchText("");
  };

  const hasFilters = selectedChannelId || selectedClientId || selectedStatus || searchText.trim() !== "";

  // Construir URL para Nova Campanha com contexto dos filtros
  const novaCampanhaUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (selectedChannelId) params.set("channelId", selectedChannelId.toString());
    if (selectedClientId) params.set("clientId", selectedClientId.toString());
    const queryString = params.toString();
    return `/questionarios/nova${queryString ? `?${queryString}` : ""}`;
  }, [selectedChannelId, selectedClientId]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "DRAFT":
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />Rascunho</Badge>;
      case "ACTIVE":
        return <Badge variant="default" className="bg-green-600"><CheckCircle2 className="w-3 h-3 mr-1" />Ativa</Badge>;
      case "CLOSED":
        return <Badge variant="outline"><XCircle className="w-3 h-3 mr-1" />Encerrada</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const formatDate = (date: string | null) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("pt-BR");
  };

  return (
    <SalviaLayout title="Questionários">
      <div className="container mx-auto py-8 space-y-6">
        {/* Header com botão Nova Campanha SEMPRE visível */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Questionários</h1>
            <p className="text-muted-foreground mt-2">
              Gerencie campanhas de aplicação de questionários ERGOS
            </p>
          </div>
          {canManageCampaigns && (
            <Button onClick={() => setLocation(novaCampanhaUrl)} size="lg">
              <Plus className="w-4 h-4 mr-2" />
              Nova Campanha
            </Button>
          )}
        </div>

        {/* Card de Filtros */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <CardTitle className="text-lg">Filtros</CardTitle>
              </div>
              {hasFilters && (
                <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                  <X className="w-4 h-4 mr-1" />
                  Limpar filtros
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {/* Campo de busca geral */}
              <div className="w-full md:w-80">
                <label className="text-sm font-medium mb-1 block">Buscar canal ou cliente</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Digite para buscar..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    className="pl-9 pr-9"
                  />
                  {searchText && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                      onClick={() => setSearchText("")}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Filtro de Canal (somente Admin Sálvia) */}
              {isAdminSalvia && (
                <div className="w-64">
                  <label className="text-sm font-medium mb-1 block">Canal</label>
                  {loadingChannels ? (
                    <div className="text-sm text-muted-foreground py-2">Carregando...</div>
                  ) : (
                    <ComboboxSelect
                      options={[
                        { value: "all", label: "Todos os canais" },
                        ...(channels?.map((channel: any) => ({
                          value: channel.id.toString(),
                          label: channel.name,
                        })) || []),
                      ]}
                      value={selectedChannelId?.toString() || "all"}
                      onValueChange={(value) => {
                        setSelectedChannelId(value === "all" ? null : Number(value));
                        setSelectedClientId(null);
                      }}
                      placeholder="Todos os canais"
                      searchPlaceholder="Buscar canal..."
                      emptyText="Nenhum canal encontrado."
                    />
                  )}
                </div>
              )}

              {/* Filtro de Cliente */}
              <div className="w-64">
                <label className="text-sm font-medium mb-1 block">Cliente</label>
                {loadingClients ? (
                  <div className="text-sm text-muted-foreground py-2">Carregando...</div>
                ) : (
                  <ComboboxSelect
                    options={[
                      { value: "all", label: "Todos os clientes" },
                      ...(clients.map((client: any) => ({
                        value: client.id.toString(),
                        label: client.nomeFantasia,
                      })) || []),
                    ]}
                    value={selectedClientId?.toString() || "all"}
                    onValueChange={(value) => setSelectedClientId(value === "all" ? null : Number(value))}
                    placeholder="Todos os clientes"
                    searchPlaceholder="Buscar cliente..."
                    emptyText="Nenhum cliente encontrado."
                    disabled={isAdminSalvia && !selectedChannelId && clients.length === 0}
                  />
                )}
              </div>

              {/* Filtro de Status */}
              <div className="w-48">
                <label className="text-sm font-medium mb-1 block">Status</label>
                <Select
                  value={selectedStatus || "all"}
                  onValueChange={(value) => setSelectedStatus(value === "all" ? null : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="DRAFT">Rascunho</SelectItem>
                    <SelectItem value="ACTIVE">Ativa</SelectItem>
                    <SelectItem value="CLOSED">Encerrada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Listagem de Campanhas */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Campanhas de Questionários</CardTitle>
                <CardDescription>
                  {totalCampaigns} campanha{totalCampaigns !== 1 ? "s" : ""} encontrada{totalCampaigns !== 1 ? "s" : ""}
                  {hasFilters && " (filtrado)"}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loadingCampaigns ? (
              <div className="text-center py-8 text-muted-foreground">Carregando campanhas...</div>
            ) : campaignsError ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Erro ao carregar campanhas: {campaignsError.message}
                </AlertDescription>
              </Alert>
            ) : campaigns.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-muted-foreground mb-4">
                  {hasFilters 
                    ? "Nenhuma campanha encontrada com os filtros aplicados"
                    : "Nenhuma campanha cadastrada ainda"
                  }
                </div>
                {canManageCampaigns && (
                  <Button onClick={() => setLocation(novaCampanhaUrl)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Criar Primeira Campanha
                  </Button>
                )}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Campanha</TableHead>
                    <TableHead>Status</TableHead>
                    {isAdminSalvia && <TableHead>Canal</TableHead>}
                    <TableHead>Cliente</TableHead>
                    <TableHead>Template</TableHead>
                    <TableHead>kMin</TableHead>
                    <TableHead>Encerramento</TableHead>
                    <TableHead>Respostas</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaigns.map((campaign: any) => (
                    <TableRow key={campaign.id}>
                      <TableCell className="font-medium">
                        <div>
                          <div>#{campaign.id}</div>
                          <div className="text-xs text-muted-foreground">
                            {formatDate(campaign.createdAt)}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(campaign.status)}</TableCell>
                      {isAdminSalvia && (
                        <TableCell>
                          <span className="text-sm">{campaign.channelName || "-"}</span>
                        </TableCell>
                      )}
                      <TableCell>
                        <div>
                          <div className="font-medium">{campaign.clientName || "-"}</div>
                          <div className="text-xs text-muted-foreground">{campaign.clientCnpj || ""}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {campaign.templateId ? (
                            <>
                              <div>ID {campaign.templateId}</div>
                              <div className="text-muted-foreground">{campaign.templateVersion}</div>
                            </>
                          ) : (
                            <span className="text-muted-foreground">Não definido</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{campaign.kMin}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{formatDate(campaign.endsAt)}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{campaign.totalResponses || 0}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {/* Botão Ativar (DRAFT) - somente admin/channel_admin */}
                          {canManageCampaigns && campaign.status === "DRAFT" && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="inline-block">
                                    <Button
                                      variant="default"
                                      size="sm"
                                      onClick={() => handleActivate(campaign.id)}
                                      disabled={activateMutation.isPending || clientsWithActiveCampaign.has(campaign.companyId)}
                                      className={clientsWithActiveCampaign.has(campaign.companyId) ? "pointer-events-none" : ""}
                                    >
                                      <Play className="w-4 h-4 mr-1" />
                                      Ativar
                                    </Button>
                                  </span>
                                </TooltipTrigger>
                                {clientsWithActiveCampaign.has(campaign.companyId) && (
                                  <TooltipContent>
                                    <p className="max-w-xs">
                                      Não é possível ativar porque já existe uma campanha ativa para este cliente.
                                    </p>
                                  </TooltipContent>
                                )}
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          {/* Botão Copiar Link (ACTIVE) */}
                          {campaign.status === "ACTIVE" && campaign.publicToken && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleCopyLink(campaign.publicToken)}
                            >
                              <Copy className="w-4 h-4 mr-1" />
                              Copiar Link
                            </Button>
                          )}
                          {/* Botão Encerrar Campanha (ACTIVE) - somente admin/channel_admin */}
                          {canManageCampaigns && campaign.status === "ACTIVE" && (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleClose(campaign.id, campaign.clientName || `#${campaign.id}`)}
                              disabled={closeMutation.isPending}
                            >
                              <StopCircle className="w-4 h-4 mr-1" />
                              Encerrar
                            </Button>
                          )}
                          {/* Botão Detalhes (sempre) */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setLocation(`/questionarios/${campaign.id}`)}
                          >
                            <ExternalLink className="w-4 h-4 mr-1" />
                            Detalhes
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Modal de Confirmação de Ativação */}
      <ConfirmActivateCampaignDialog
        open={confirmDialogOpen}
        onOpenChange={setConfirmDialogOpen}
        onConfirm={handleConfirmActivate}
        isLoading={activateMutation.isPending}
      />

      {/* Modal de Confirmação de Encerramento */}
      <Dialog open={closeDialogOpen} onOpenChange={(open) => {
        if (!closeMutation.isPending) {
          setCloseDialogOpen(open);
          if (!open) setCampaignToClose(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <StopCircle className="w-5 h-5" />
              Encerrar Campanha
            </DialogTitle>
            <DialogDescription className="pt-2">
              Você está prestes a encerrar a campanha do cliente{" "}
              <strong>{campaignToClose?.clientName}</strong>.
            </DialogDescription>
            <div className="space-y-3 pt-2">
              <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-md p-3 text-sm text-amber-800 dark:text-amber-200">
                <p className="font-semibold mb-1">Esta ação é irreversível:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Novas respostas serão bloqueadas</li>
                  <li>A consolidação por moda será executada automaticamente</li>
                  <li>As pendências de avaliação serão atualizadas</li>
                </ul>
              </div>
            </div>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setCloseDialogOpen(false);
                setCampaignToClose(null);
              }}
              disabled={closeMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmClose}
              disabled={closeMutation.isPending}
            >
              {closeMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Encerrando...
                </>
              ) : (
                <>
                  <StopCircle className="w-4 h-4 mr-2" />
                  Confirmar Encerramento
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SalviaLayout>
  );
}
